import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { CreditAdjustment, UpdateBalanceUsageRequest } from '../../types/firm';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

// Available credit types
const CREDIT_TYPES = [
  { value: 'QUICK_DEAL', label: 'Quick Deal' },
  { value: 'EMAIL_REVEAL', label: 'Email Reveal' },
  { value: 'AI_QUERY', label: 'AI Query' },
  { value: 'NDA_SIGNATURE', label: 'NDA Signature' },
  { value: 'GENERAL', label: 'General' },
] as const;

type BalanceUsageFormProps = {
  onSubmit: (data: UpdateBalanceUsageRequest) => Promise<void>;
  onCancel: () => void;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  successMessage: string | null;
};

export function BalanceUsageForm({
  onSubmit,
  onCancel,
  isPending,
  isError,
  error,
  successMessage,
}: BalanceUsageFormProps) {
  const [adjustments, setAdjustments] = useState<CreditAdjustment[]>([
    { creditType: '', amount: 0, description: '' },
  ]);

  const handleAddAdjustment = () => {
    setAdjustments([...adjustments, { creditType: '', amount: 0, description: '' }]);
  };

  const handleRemoveAdjustment = (index: number) => {
    setAdjustments(adjustments.filter((_, i) => i !== index));
  };

  const handleAdjustmentChange = (index: number, field: keyof CreditAdjustment, value: string | number) => {
    const updated = [...adjustments];
    updated[index] = { ...updated[index], [field]: value };
    setAdjustments(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validAdjustments = adjustments.filter(
      (adj) => adj.creditType.trim() !== '' && adj.amount !== 0
    );
    
    if (validAdjustments.length === 0) {
      return;
    }

    await onSubmit({
      adjustments: validAdjustments.map((adj) => ({
        creditType: adj.creditType,
        amount: adj.amount,
        ...(adj.description && { description: adj.description }),
      })),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="rounded-lg bg-surface-secondary p-4">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-text-primary">Credit Adjustments</h4>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddAdjustment}
            className="h-8"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Adjustment
          </Button>
        </div>

        <div className="space-y-4">
          {adjustments.map((adjustment, index) => (
            <div key={index} className="rounded-md border border-stroke-primary bg-background p-4">
              <div className="mb-4 flex items-center justify-between">
                <h5 className="text-sm font-medium text-text-primary">Adjustment {index + 1}</h5>
                {adjustments.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAdjustment(index)}
                    className="h-8 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <Label htmlFor={`creditType-${index}`} className="mb-2 block text-xs">
                    Credit Type *
                  </Label>
                  <select
                    id={`creditType-${index}`}
                    value={adjustment.creditType}
                    onChange={(e) => handleAdjustmentChange(index, 'creditType', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    required
                  >
                    <option value="">Select credit type</option>
                    {CREDIT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor={`amount-${index}`} className="mb-2 block text-xs">
                    Amount *
                  </Label>
                  <Input
                    id={`amount-${index}`}
                    type="number"
                    value={adjustment.amount || ''}
                    onChange={(e) => handleAdjustmentChange(index, 'amount', parseInt(e.target.value) || 0)}
                    placeholder="Positive or negative"
                    required
                  />
                  <p className="mt-1 text-xs text-text-secondary">
                    Positive grants credits, negative revokes credits
                  </p>
                </div>

                <div>
                  <Label htmlFor={`description-${index}`} className="mb-2 block text-xs">
                    Description (optional)
                  </Label>
                  <Input
                    id={`description-${index}`}
                    type="text"
                    value={adjustment.description || ''}
                    onChange={(e) => handleAdjustmentChange(index, 'description', e.target.value)}
                    placeholder="Reason for adjustment"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {successMessage && (
        <div className="rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}

      {isError && error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-600">{error.message || 'Failed to update balance usage'}</p>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending || adjustments.filter((adj) => adj.creditType.trim() !== '' && adj.amount !== 0).length === 0}>
          {isPending ? 'Updating...' : 'Update Balance Usage'}
        </Button>
      </div>
    </form>
  );
}

